<template>
  <v-container fluid class="px-6 pt-0">
    <!-- SEARCH FORM -->
    <div class="sticky-banner">
      <v-card class="overflow-hidden" color="primary" :loading="isLoading" dark>
        <template v-slot:progress>
          <v-progress-linear
            color="primary lighten-3"
            height="4"
            indeterminate
            absolute
          ></v-progress-linear>
        </template>
        <v-form class="mt-2">
          <v-container fluid>
            <!-- Basic search fields -->
            <v-row>
              <v-col cols="12" md="3" sm="6">
                <v-text-field
                  ref="accessionNo"
                  v-model="accessionNo"
                  v-on:keyup.13="search"
                  :label="$t('Accession Number')"
                  color="shades white"
                  dense
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <v-text-field
                  v-model="patName"
                  v-on:keyup.13="search"
                  :label="$t('Patient Name')"
                  color="shades white"
                  dense
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <v-text-field
                  v-model="patId"
                  v-on:keyup.13="search"
                  :label="$t('Patient Id')"
                  color="shades white"
                  dense
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" md="3" sm="6">
                <v-menu
                  ref="dateMenu"
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :return-value.sync="studyDates"
                  :transition="false"
                  min-width="290px"
                  offset-y
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateRangeText"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      v-on:keydown.enter.tab="isExpanded = true"
                      :label="$t('Study Date')"
                      color="shades white"
                      dense
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="studyDates"
                    :locale="$store.getters.locale"
                    color="primary"
                    scrollable
                    no-title
                    range
                  >
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      color="red lighten-2"
                      @click="
                        $refs.dateMenu.save([]);
                        preset = '';
                      "
                      >{{ $t("clear") }}</v-btn
                    >
                    <v-btn text color="primary" @click="dateMenu = false">{{
                      $t("cancel")
                    }}</v-btn>
                    <v-btn
                      text
                      color="primary"
                      @click="saveDates($refs.dateMenu)"
                      >{{ $t("accept") }}</v-btn
                    >
                  </v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Secondary search fields -->
            <transition name="fade">
              <v-row v-if="isExpanded">
                <!-- Modalities -->
                <v-col cols="12" md="3" sm="6">
                  <v-autocomplete
                    v-model="modsInStudy"
                    ref="modsRef"
                    :items="modsItems"
                    :label="$t('Modalities')"
                    color="shades white"
                    hide-selected
                    clearable
                    multiple
                    dense
                  >
                  </v-autocomplete>
                </v-col>

                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="studyId"
                    v-on:keyup.13="search"
                    :label="$t('Study Id')"
                    color="shades white"
                    dense
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="studyDesc"
                    v-on:keyup.13="search"
                    :label="$t('Study Description')"
                    color="shades white"
                    dense
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="instName"
                    v-on:keyup.13="search"
                    :label="$t('Institution Name')"
                    color="shades white"
                    dense
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="refPhysician"
                    v-on:keyup.13="search"
                    :label="$t('Referring Physician')"
                    color="shades white"
                    dense
                  >
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="stationName"
                    v-on:keyup.13="search"
                    v-on:keydown.enter.tab="lastTab"
                    :label="$t('Station Name')"
                    color="shades white"
                    dense
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </transition>
          </v-container>
        </v-form>

        <v-divider></v-divider>

        <!-- Desktop -->
        <v-card-actions class="hidden-md-and-down">
          <v-btn v-if="preset === 'today'" color="primary lighten-1">
            {{ $t("today") }}
          </v-btn>
          <v-btn v-else color="primary darken-1" @click="setPreset('today')">
            {{ $t("today") }}
          </v-btn>
          <v-btn v-if="preset === 'last week'" color="primary lighten-1">
            {{ $t("last week search") }}
          </v-btn>
          <v-btn
            v-else
            color="primary darken-1"
            @click="setPreset('last week')"
          >
            {{ $t("last week search") }}
          </v-btn>
          <v-btn v-if="preset === 'last month'" color="primary lighten-1">
            {{ $t("last month search") }}
          </v-btn>
          <v-btn
            v-else
            color="primary darken-1"
            @click="setPreset('last month')"
          >
            {{ $t("last month search") }}
          </v-btn>
          <v-btn v-if="preset === 'last year'" color="primary lighten-1">
            {{ $t("last year search") }}
          </v-btn>
          <v-btn
            v-else
            color="primary darken-1"
            @click="setPreset('last year')"
          >
            {{ $t("last year search") }}
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            color="primary darken-1"
            @click="isExpanded = !isExpanded"
            :style="expandStyle"
          >
            <div v-if="isExpanded">
              <v-icon left>mdi-chevron-up</v-icon>
              {{ $t("less") }}
            </div>
            <div v-else>
              <v-icon left>mdi-chevron-down</v-icon>
              {{ $t("more") }}
            </div>
          </v-btn>

          <v-btn color="primary darken-1" @click="openJobsModal">
            <v-icon left>mdi-format-list-bulleted</v-icon>
            {{ $t("jobs") }}
          </v-btn>

          <div class="text-center px-2">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary darken-1"
                  v-bind="attrs"
                  v-on="on"
                  :style="archiveStyle"
                >
                  <v-icon left v-if="archive == 'LOCAL'"
                    >mdi-database-outline</v-icon
                  >
                  <v-icon left v-else>mdi-lan-pending</v-icon>
                  {{ archive }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in achiveItems"
                  :key="index"
                  @click="selectArchive(item.name)"
                >
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <v-btn color="primary darken-1" @click="clearAll">
            <v-icon left>mdi-reload</v-icon>
            {{ $t("clear") }}
          </v-btn>

          <template v-if="!isLoading">
            <v-badge
              color="error"
              icon="mdi-alert-circle-outline"
              overlap
              class="ml-4 mr-2"
              v-if="hiddenFields && !isExpanded"
            >
              <v-btn color="primary darken-1" @click="search(true)" width="110">
                <v-icon left>mdi-magnify</v-icon>
                {{ $t("search") }}
              </v-btn>
            </v-badge>
            <v-btn
              color="primary darken-1"
              @click="search(true)"
              width="110"
              v-else
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t("search") }}
            </v-btn>
          </template>
          <v-btn
            color="red darken-2"
            class="ml-4 mr-2"
            @click="stopSearch()"
            width="110"
            v-else
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            {{ $t("stop") }}
          </v-btn>
        </v-card-actions>
        <!-- /Desktop -->

        <!-- Middle -->
        <v-card-actions class="hidden-lg-and-up hidden-sm-and-down">
          <v-spacer></v-spacer>

          <v-btn
            color="primary darken-1"
            @click="isExpanded = !isExpanded"
            :style="expandStyle"
          >
            <div v-if="isExpanded">
              <v-icon left>mdi-chevron-up</v-icon>
              {{ $t("less") }}
            </div>
            <div v-else>
              <v-icon left>mdi-chevron-down</v-icon>
              {{ $t("more") }}
            </div>
          </v-btn>

          <v-btn color="primary darken-1" @click="openJobsModal">
            <v-icon left>mdi-format-list-bulleted</v-icon>
            {{ $t("jobs") }}
          </v-btn>

          <div class="text-center px-2">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary darken-1"
                  v-bind="attrs"
                  v-on="on"
                  :style="archiveStyle"
                >
                  <v-icon left v-if="archive == 'LOCAL'"
                    >mdi-database-outline</v-icon
                  >
                  <v-icon left v-else>mdi-lan-pending</v-icon>
                  {{ archive }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in achiveItems"
                  :key="index"
                  @click="selectArchive(item.name)"
                >
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <v-btn color="primary darken-1" @click="clearAll">
            <v-icon left>mdi-reload</v-icon>
            {{ $t("clear") }}
          </v-btn>

          <template v-if="!isLoading">
            <v-badge
              color="error"
              icon="mdi-alert-circle-outline"
              overlap
              class="ml-4 mr-2"
              v-if="hiddenFields && !isExpanded"
            >
              <v-btn color="primary darken-1" @click="search(true)" width="110">
                <v-icon left>mdi-magnify</v-icon>
                {{ $t("search") }}
              </v-btn>
            </v-badge>
            <v-btn
              color="primary darken-1"
              @click="search(true)"
              width="110"
              v-else
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t("search") }}
            </v-btn>
          </template>
          <v-btn
            color="red darken-2"
            class="ml-4 mr-2"
            @click="stopSearch()"
            width="110"
            v-else
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            {{ $t("stop") }}
          </v-btn>
        </v-card-actions>
        <!-- /Middle -->

        <!-- Mobile -->
        <v-card-actions class="hidden-md-and-up">
          <v-spacer></v-spacer>

          <div class="text-center px-2">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary darken-1"
                  v-bind="attrs"
                  v-on="on"
                  :style="archiveStyle"
                >
                  <v-icon left v-if="archive == 'LOCAL'"
                    >mdi-database-outline</v-icon
                  >
                  <v-icon left v-else>mdi-lan-pending</v-icon>
                  {{ archive }}
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in achiveItems"
                  :key="index"
                  @click="selectArchive(item.name)"
                >
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <template v-if="!isLoading">
            <v-badge
              color="error"
              icon="mdi-alert-circle-outline"
              overlap
              class="ml-4 mr-2"
              v-if="hiddenFields && !isExpanded"
            >
              <v-btn color="primary darken-1" @click="search(true)" width="110">
                <v-icon left>mdi-magnify</v-icon>
                {{ $t("search") }}
              </v-btn>
            </v-badge>
            <v-btn
              color="primary darken-1"
              @click="search(true)"
              width="110"
              v-else
            >
              <v-icon left>mdi-magnify</v-icon>
              {{ $t("search") }}
            </v-btn>
          </template>
          <v-btn
            color="red darken-2"
            class="ml-4 mr-2"
            @click="stopSearch()"
            width="110"
            v-else
          >
            <v-icon left>mdi-close-circle-outline</v-icon>
            {{ $t("stop") }}
          </v-btn>
        </v-card-actions>
        <!-- /Mobile -->
      </v-card>
    </div>

    <!-- MAIN TABLE -->
    <v-sheet class="mt-2 mx-1" elevation="1">
      <v-data-table
        :headers="studiesTableHeaders"
        :items="studiesTableItems"
        :options.sync="studiesOptions"
        :expanded.sync="expanded.table"
        @click:row="clickTableRow"
        :item-key="studyItemKey"
        :server-items-length="totalStudies"
        disable-pagination
        hide-default-footer
        dense
      >
        <!-- Edit and send icons -->
        <template
          v-slot:[`item.endIcons`]="{ item }"
          v-if="archive === 'LOCAL'"
        >
          <v-btn icon v-on:click.stop="share(item)" small>
            <v-icon small>mdi-link</v-icon>
          </v-btn>
          <v-btn icon v-on:click.stop="download($event, item)" small>
            <v-icon small>mdi-download</v-icon>
          </v-btn>
          <v-btn icon v-on:click.stop="editItem(item)" small v-if="editMode">
            <v-icon small>mdi-file-edit</v-icon>
          </v-btn>
          <v-btn icon v-on:click.stop="sendStudy(item)" small>
            <v-icon small>mdi-send</v-icon>
          </v-btn>
        </template>

        <template v-slot:[`item.startIcons`]="{ item }">
          <!-- Stable icon -->
          <template v-if="archive === 'LOCAL'">
            <v-icon
              small
              color="green"
              class="mr-2"
              v-if="item.isStable && typeof item.error == 'undefined'"
            >
              mdi-shield-check-outline
            </v-icon>
            <v-tooltip top v-else-if="!(typeof item.error == 'undefined')">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small color="red" class="mr-2" v-bind="attrs" v-on="on">
                  mdi-shield-alert-outline
                </v-icon>
              </template>
              <span>{{ $t(item.error) }}</span>
            </v-tooltip>
            <v-icon small class="mr-2" v-else> mdi-shield-outline </v-icon>
          </template>

          <!-- Series icon -->
          <v-btn
            icon
            small
            v-on:click.stop="toggleSeriesItem(item)"
            v-if="seriesChevron(item)"
          >
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>
          <v-btn icon small v-else v-on:click.stop="toggleSeriesItem(item)">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <template v-slot:[`expanded-item`]="{ headers }">
          <td :colspan="headers.length" class="pt-1 pb-1">
            <v-data-table
              :headers="seriesTableHeader"
              :items="seriesTableItems"
              :loading="seriesTableLoading"
              :items-per-page="Number(100)"
              :sort-by="['seriesNo', 'seriesTime']"
              hide-default-footer
              dense
            ></v-data-table>
          </td>
        </template>

        <!-- Table empty icon -->
        <template slot="no-data">
          <v-icon
            style="font-size: 92px; opacity: 40%"
            class="py-2"
            v-if="archive === 'LOCAL'"
            >mdi-database-search-outline</v-icon
          >
          <v-icon style="font-size: 92px; opacity: 40%" class="py-2" v-else
            >mdi-lan-pending</v-icon
          >
        </template>
      </v-data-table>

      <div class="py-1 primary" v-if="lastLine"></div>
      <div class="py-1" v-else></div>
    </v-sheet>

    <SendModal
      v-bind:studyData="studyDataSend"
      @onShowJobs="
        showJobsSnackbar = true;
        showJobsModal = false;
      "
    />

    <ViewModal
      v-bind:studyData="studyDataView"
      v-bind:dicomViewers="dicomViewers"
    />

    <LinkModal
      v-bind:studyData="studyDataLink"
      v-bind:dicomViewers="dicomViewers"
    />

    <RetrieveModal
      v-bind:studyData="studyDataRetrieve"
      @onShowJobs="
        showJobsSnackbar = true;
        showJobsModal = false;
      "
    />

    <JobsModal
      v-if="showJobsModal"
      v-bind:websocketConfig="websocketConfig"
      @onHideJobsModal="showJobsModal = false"
    />

    <JobsSnackbar
      v-if="showJobsSnackbar"
      v-bind:websocketConfig="websocketConfig"
      @onHideJobsSnackbar="showJobsSnackbar = false"
      @onViewStudy="viewStudy"
    />
  </v-container>
</template>

<script>
import axios from "axios";
import i18n from "../../i18n";
import EventBus from "../../event-bus";
import Modalities from "../../lists/modalities";

import SendModal from "./Search/SendModal.vue";
import ViewModal from "./Search/ViewModal.vue";
import LinkModal from "./Search/LinkModal.vue";
import JobsModal from "./Search/JobsModal.vue";
import RetrieveModal from "./Search/RetrieveModal.vue";
import JobsSnackbar from "./Search/JobsSnackbar.vue";

const STUDIES_LIMIT = 50;

const CancelToken = axios.CancelToken;

export default {
  name: "StudiesSearch",
  props: {
    form: Object,
  },
  components: {
    SendModal,
    ViewModal,
    LinkModal,
    JobsModal,
    RetrieveModal,
    JobsSnackbar,
  },
  data: () => ({
    // Values
    accessionNo: "",
    patName: "",
    patId: "",
    studyDates: [],
    modsInStudy: [],
    studyId: "",
    instName: "",
    stationName: "",
    studyDesc: "",
    refPhysician: "",
    modsItems: Modalities,
    // Configuration
    cancelToken: null,
    dicomViewers: null,
    dateMenu: false,
    isLoading: false,
    preset: "today",
    editItems: [],
    // Expand Search Menu
    isExpanded: false,
    // Archives
    archives: [],
    archive: "LOCAL",
    archiveStyle: "color:rgb(255,255,255);",
    // Jobs
    websocketConfig: {
      ssl: false,
      port: 0,
    },
    // Studies Table
    studies: [],
    limit: STUDIES_LIMIT,
    offset: 0,
    emptyResponse: false,
    studyItemKey: "studyDbId",
    studiesTableHeaders: [
      { text: "", value: "startIcons", width: "90px" },
      { text: i18n.t("Accession Number"), value: "accessionNo" },
      { text: i18n.t("Patient Name"), value: "patName" },
      { text: i18n.t("Patient Id"), value: "patId" },
      {
        text: i18n.t("Study Date"),
        value: "studyDate",
        sort: (a, b) => {
          if (a.length === 10 && b.length === 10) {
            let ad = Number(a.slice(6) + a.slice(3, -5) + a.slice(0, -8));
            let bd = Number(b.slice(6) + b.slice(3, -5) + b.slice(0, -8));

            if (ad > bd) return -1;
            if (ad < bd) return 1;
            if (ad == bd) return 0;
          } else {
            return 0;
          }
        },
      },
      { text: i18n.t("Modalities"), value: "modsInStudy", sortable: false },
      { text: i18n.t("Study Id"), value: "studyId" },
      { text: i18n.t("Study Description"), value: "studyDesc" },
      { text: "", value: "endIcons", width: "150px", sortable: false },
    ],
    studiesTableItems: [],
    studiesOptions: {},
    totalStudies: null,
    editMode: false,
    // Series Table
    expanded: {
      table: [],
      studyDbId: null,
      studyIuid: null,
      patId: null,
    },
    seriesTableHeader: [
      { text: i18n.t("Series Number"), value: "seriesNo" },
      { text: i18n.t("Series Time"), value: "seriesTime" },
      { text: i18n.t("Series Date"), value: "seriesDate" },
      { text: i18n.t("# Images"), value: "seriesInstCount" },
      { text: i18n.t("Body Part"), value: "bodyPart" },
      { text: i18n.t("Modality"), value: "modality" },
      { text: i18n.t("Station Name"), value: "stationName" },
      { text: i18n.t("Institution Name"), value: "instName" },
      { text: i18n.t("Series Description"), value: "seriesDesc" },
    ],
    seriesTableLoading: false,
    seriesTableItems: [],
    // Modals & Snackbars
    showJobsModal: false,
    showJobsSnackbar: false,
    studyDataSend: {
      data: null,
      activate: false,
    },
    studyDataView: {
      data: null,
      activate: false,
    },
    studyDataLink: {
      data: null,
      activate: false,
    },
    studyDataRetrieve: {
      data: null,
      activate: false,
    },
  }),
  watch: {
    // Execute table changes. First change avoided.
    studiesOptions: {
      handler(_val, oldVal) {
        if (typeof oldVal.groupBy != "undefined") this.search(true);
      },
      deep: true,
    },
  },
  computed: {
    hiddenFields() {
      if (
        this.modsInStudy != "" ||
        this.studyId != "" ||
        this.studyDesc != "" ||
        this.instName != "" ||
        this.refPhysician != "" ||
        this.stationName != ""
      ) {
        return true;
      } else {
        return false;
      }
    },
    expandStyle() {
      if (
        this.modsInStudy != "" ||
        this.studyId != "" ||
        this.studyDesc != "" ||
        this.instName != "" ||
        this.refPhysician != "" ||
        this.stationName != ""
      ) {
        return "color:rgb(255,170,170);";
      } else {
        return "color:rgb(255,255,255);";
      }
    },
    lastLine() {
      if (this.emptyResponse && this.studies.length != 0) {
        return true;
      } else {
        return false;
      }
    },
    achiveItems() {
      var list = [
        {
          name: "LOCAL",
        },
      ];
      this.archives.forEach((element) => {
        list.push({
          name: element,
        });
      });
      return list;
    },
    dateRangeText() {
      this.flipDates();

      // format date display
      let fmtDate0 = this.formatDate(this.studyDates[0]);
      let fmtDate1 = this.formatDate(this.studyDates[1]);

      if (fmtDate0 != null) {
        if (fmtDate1 == null) {
          return fmtDate0 + "~" + fmtDate0;
        } else {
          return fmtDate0 + "~" + fmtDate1;
        }
      } else {
        return "";
      }
    },
    studyEditPatient: function () {
      if (this.editItems.length > 0) {
        return this.editItems[0].patName;
      } else {
        return "";
      }
    },
  },
  methods: {
    sendError(error) {
      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    setPreset(preset) {
      let now = new Date();
      let now_iso = this.toISODate(now);

      if (preset === "today") {
        this.studyDates = [now_iso, now_iso];
      } else if (preset === "last week") {
        let old = new Date();
        old.setDate(now.getDate() - 7);
        this.studyDates = [this.toISODate(old), now_iso];
      } else if (preset === "last month") {
        let old = new Date();
        old.setDate(now.getDate() - 31);
        this.studyDates = [this.toISODate(old), now_iso];
      } else if (preset === "last year") {
        let old = new Date();
        old.setDate(now.getDate() - 365);
        this.studyDates = [this.toISODate(old), now_iso];
      }

      this.preset = preset;
      this.search(true);
    },
    openJobsModal() {
      this.showJobsSnackbar = false;
      this.showJobsModal = true;
    },
    flipDates() {
      let date0 = new Date(this.studyDates[0]);
      let date1 = new Date(this.studyDates[1]);

      if (date0 > date1) {
        let tmp = this.studyDates[0];
        this.studyDates[0] = this.studyDates[1];
        this.studyDates[1] = tmp;
      }
    },
    toISODate(src) {
      var yyyy = src.getFullYear();
      var mm = String(src.getMonth() + 1).padStart(2, "0");
      var dd = String(src.getDate()).padStart(2, "0");

      return yyyy + "-" + mm + "-" + dd;
    },
    saveDates(dateMenu) {
      this.preset = "";
      if (this.studyDates.length == 1) {
        this.studyDates[1] = this.studyDates[0];
      }

      dateMenu.save(this.studyDates);
    },
    addWildcards(value) {
      if (value !== "") {
        return "*" + value + "*";
      } else {
        return "";
      }
    },
    search(firstSearch) {
      this.isLoading = true;

      if (firstSearch) {
        this.offset = 0;
        this.limit = STUDIES_LIMIT;
        this.emptyResponse = false;
      }

      let data = {
        accessionNo: this.addWildcards(this.accessionNo),
        patName: this.addWildcards(this.patName),
        patId: this.addWildcards(this.patId),
        studyDate: this.formatDicomDate(this.studyDates),
        modsInStudy: this.modsInStudy,
        studyId: this.addWildcards(this.studyId),
        studyDesc: this.addWildcards(this.studyDesc),
        instName: this.addWildcards(this.instName),
        stationName: this.addWildcards(this.stationName),
        refPhysician: this.addWildcards(this.refPhysician),
        limit: this.limit,
        offset: this.offset,
      };

      // Sort options
      if (this.archive === "LOCAL") {
        if (
          typeof this.studiesOptions.sortBy != "undefined" &&
          this.studiesOptions.sortBy.length
        ) {
          let sortBy = this.studiesOptions.sortBy[0];

          let sortDesc = false;
          if (this.studiesOptions.sortDesc.length) {
            sortDesc = this.studiesOptions.sortDesc[0];
          }

          data.sort = {
            by: sortBy,
            desc: sortDesc,
          };
        }
      }

      // Save search form
      if (firstSearch) {
        this.$emit("onSaveForm", {
          accessionNo: this.accessionNo,
          patName: this.patName,
          patId: this.patId,
          studyDate: this.studyDates,
          modsInStudy: this.modsInStudy,
          studyId: this.studyId,
          studyDesc: this.studyDesc,
          instName: this.instName,
          stationName: this.stationName,
          refPhysician: this.refPhysician,
          preset: this.preset,
          archive: this.archive,
        });
      }

      // Use API depending on archive type
      let url;
      if (this.archive === "LOCAL") {
        url = "/api/v1/dicom/find/studies";
        this.studyItemKey = "studyDbId";
      } else {
        data.archive = this.archive;
        delete data.limit;
        delete data.offset;
        url = "/api/v1/dicom/find/archives/studies";
        this.studyItemKey = "studyIuid";
      }

      // New cancel token
      this.cancelToken = CancelToken.source();

      // Main study search
      axios({
        url: url,
        method: "POST",
        data: data,
        cancelToken: this.cancelToken.token,
      })
        .then((response) => {
          this.isLoading = false;
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            this.expanded = {
              table: [],
              studyDbId: null,
              studyIuid: null,
              patId: null,
            };

            if (response.data.studies.length == 0) {
              this.emptyResponse = true;
            }

            if (firstSearch) {
              this.studies = response.data.studies;
            } else {
              this.studies.push(...response.data.studies);
            }

            this.writeTable(this.studies);
          }
        })
        .catch((error) => {
          this.isLoading = false;
          if (!axios.isCancel(error)) {
            this.sendError(error);
          }
        });
    },
    stopSearch() {
      this.cancelToken.cancel();
    },
    writeTable(src) {
      function fmtDate(src) {
        if (typeof src !== "undefined" && src !== null && src.length == 8) {
          return (
            src.substring(6, 8) +
            "/" +
            src.substring(4, 6) +
            "/" +
            src.substring(0, 4)
          );
        } else {
          return "";
        }
      }

      function rmCarets(src) {
        if (typeof src !== "undefined") {
          return src.replaceAll("^", " ").trim();
        } else {
          return "";
        }
      }

      for (var i = 0; i < src.length; i++) {
        if (typeof src[i].editSelected == "undefined") {
          src[i].patName = this.limitSize(rmCarets(src[i].patName), 30);
          src[i].patId = this.limitSize(src[i].patId, 30);
          src[i].studyId = this.limitSize(src[i].studyId, 30);
          src[i].studyDesc = this.limitSize(src[i].studyDesc, 40);
          src[i].modsInStudy = src[i].modsInStudy.join("\\");
          src[i].studyDate = fmtDate(src[i].studyDate);
          src[i].editSelected = false;
        }
      }

      if (this.archive === "LOCAL") {
        this.totalStudies = src.length;
      } else {
        this.totalStudies = null;
      }

      this.studiesTableItems = src;
    },
    clearAll() {
      this.accessionNo = "";
      this.patName = "";
      this.patId = "";
      this.studyDates = [];
      this.modsInStudy = [];
      this.studyId = "";
      this.studyDesc = "";
      this.instName = "";
      this.stationName = "";
      this.refPhysician = "";
      this.setPreset("today");
      this.$refs.accessionNo.focus();
    },
    formatDate(date) {
      if (!date) return null;
      let [year, month, day] = date.split("-");
      year = year.slice(-2);
      return `${day}/${month}/${year}`;
    },
    formatDicomDate(source) {
      if (source.length == 2) {
        let [syear, smonth, sday] = source[0].split("-");
        let [eyear, emonth, eday] = source[1].split("-");
        return syear + smonth + sday + "-" + eyear + emonth + eday;
      } else {
        return "";
      }
    },
    lastTab(err) {
      if (
        !(
          this.modsInStudy == [] ||
          this.studyId ||
          this.studyDesc ||
          this.instName ||
          this.refPhysician
        )
      ) {
        this.isExpanded = false;
      }
      err.preventDefault();
      this.$refs.accessionNo.focus();
    },
    keyDown(err) {
      if (err.ctrlKey && err.keyCode == 81) {
        if (this.preset === "today") {
          this.setPreset("last week");
        } else if (this.preset === "last week") {
          this.setPreset("last month");
        } else if (this.preset === "last month") {
          this.setPreset("last year");
        } else if (this.preset === "last year") {
          this.setPreset("today");
        }
        err.preventDefault();
      }
    },
    selectArchive(archive) {
      // Change button color
      if (archive === "LOCAL") {
        this.archiveStyle = "color:rgb(255,255,255);";
      } else {
        this.archiveStyle = "color:rgb(255,170,170);";
      }

      // Select new archive and search
      this.archive = archive;
      this.setPreset("today");
    },
    // Infinite scrolling effect
    scroll() {
      if (this.archive === "LOCAL" && this.expanded.studyDbId === null) {
        const { scrollTop, scrollHeight, clientHeight } =
          document.documentElement;

        if (scrollTop + clientHeight >= scrollHeight - 5) {
          if (!this.emptyResponse) {
            this.offset += this.limit;
            this.search(false);
          }
        }
      }
      // Hide datepicker when scrolling
      if (this.dateMenu) this.dateMenu = false;
      if (typeof this.$refs.modsRef !== "undefined") {
        if (this.$refs.modsRef.isMenuActive) {
          this.$refs.modsRef.isMenuActive = false;
        }
      }
    },
    limitSize: function (src, max) {
      if (typeof src !== "undefined") {
        if (src !== null) {
          if (src.length > max) {
            return src.substring(0, max) + "..";
          } else {
            return src;
          }
        }
      }
    },
    seriesChevron: function (item) {
      return (
        item.studyDbId === this.expanded.studyDbId ||
        item.studyIuid === this.expanded.studyIuid
      );
    },
    clickTableRow(item) {
      if (this.archive === "LOCAL") {
        if (typeof item.error == "undefined") {
          this.viewStudy(item);
        } else {
          this.sendError(i18n.t("Study blocked:") + " " + i18n.t(item.error));
        }
      } else {
        item.archive = this.archive;
        this.retrieveStudy(item);
      }
    },
    toggleSeriesItem(item) {
      if (item !== this.expanded.table[0]) {
        if (this.archive === "LOCAL") {
          // Load local series
          this.loadSeries(item.studyDbId, null, null);
          this.expanded = {
            table: [item],
            studyDbId: item.studyDbId,
            studyIuid: null,
            patId: null,
          };
        } else {
          // Load remote series
          this.loadSeries(null, item.studyIuid, item.patId);
          this.expanded = {
            table: [item],
            studyDbId: null,
            studyIuid: item.studyIuid,
            patId: item.patId,
          };
        }
      } else {
        this.expanded = {
          table: [],
          studyDbId: null,
          studyIuid: null,
          patId: null,
        };
      }
    },
    loadSeries(studyDbId, studyIuid, patId) {
      this.seriesTableItems = [];
      this.seriesTableLoading = true;

      function fmtDate(src) {
        if (typeof src !== "undefined" && src !== null && src.length == 8) {
          return (
            src.substring(6, 8) +
            "/" +
            src.substring(4, 6) +
            "/" +
            src.substring(0, 4)
          );
        } else {
          return "";
        }
      }

      function fmtTime(src) {
        return (
          src.substring(0, 2) +
          ":" +
          src.substring(2, 4) +
          ":" +
          src.substring(4, 6)
        );
      }

      let url, data;
      if (this.archive == "LOCAL") {
        url = "/api/v1/dicom/find/series";
        data = {
          studyDbId: studyDbId,
        };
      } else {
        url = "/api/v1/dicom/find/archives/series";
        data = {
          studyIuid: studyIuid,
          patId: patId,
          archive: this.archive,
        };
      }

      axios({
        url: url,
        method: "POST",
        data: data,
      })
        .then((response) => {
          this.seriesTableLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            this.sendError(response.data.error);
          } else {
            var series = response.data.series;
            series.forEach((el) => {
              // change date format
              if (typeof el.seriesDate !== "undefined") {
                if (el.seriesDate !== null && el.seriesDate.length == 8) {
                  el.seriesDate = fmtDate(el.seriesDate);
                }
              }

              // change time format
              if (typeof el.seriesTime !== "undefined") {
                if (el.seriesTime !== null && el.seriesTime.length > 5) {
                  el.seriesTime = fmtTime(el.seriesTime);
                }
              }
            });

            this.seriesTableItems = series;
          }
        })
        .catch((error) => {
          this.seriesTableLoading = false;
          this.sendError(error);
        });
    },
    // Share study zip
    share(data) {
      this.studyDataLink = {
        data: data,
        activate: !this.studyDataLink.activate,
      };
    },
    // Download study zip
    download(event, item) {
      event.target.classList.add("primary--text");
      window.open("/api/v1/dicom/zip/" + item.studyDbId);
      setTimeout(() => {
        event.target.classList.remove("primary--text");
      }, 5000);
    },
    // Select studies for edition
    editItem(item) {
      let item_obj = new Object();
      item_obj.studyDbId = item.studyDbId;
      this.$emit("onEditStudy", [item_obj]);
    },
    // Open Study Send modal. Use activate to make it reactive.
    sendStudy(data) {
      this.studyDataSend = {
        data: data,
        activate: !this.studyDataSend.activate,
      };
    },
    // Open Study View modal. Use activate to make it reactive.
    viewStudy(data) {
      this.studyDataView = {
        data: data,
        activate: !this.studyDataView.activate,
      };
    },
    // Open Retrieve Study modal. Use activate to make it reactive.
    retrieveStudy(data) {
      this.studyDataRetrieve = {
        data: data,
        activate: !this.studyDataRetrieve.activate,
      };
    },
  },
  created() {
    // Websocket configuration
    axios({
      url: "/api/v1/dicom/websocket",
      method: "GET",
    })
      .then((response) => {
        if (
          !response.data ||
          !response.data.status ||
          !(response.data.status == "done")
        ) {
          this.sendError(response.data.error);
        } else {
          // Websocket Port
          if (typeof response.data.port === "undefined") {
            this.sendError("Invalid websocket port value.");
          } else {
            this.websocketConfig.port = Number(response.data.port);
          }
          // Websocket Security
          if (typeof response.data.ssl === "undefined") {
            this.sendError("Invalid websocket SSL value.");
          } else {
            this.websocketConfig.ssl = Boolean(response.data.ssl);
          }
        }
      })
      .catch((error) => {
        this.sendError(error);
      });

    // DICOM Viewers configuration
    axios({
      url: "/api/v1/dicom/viewers",
      method: "GET",
    })
      .then((response) => {
        if (
          !response.data ||
          !response.data.status ||
          !(response.data.status == "done")
        ) {
          this.sendError(response.data.error);
        } else {
          this.dicomViewers = response.data;
        }
      })
      .catch((error) => {
        this.sendError(error);
      });
  },
  mounted() {
    // Focus first input field for rapid typing
    this.$refs.accessionNo.focus();

    // Add event listeners for key input and infinite scroll
    window.addEventListener("keydown", this.keyDown);
    window.addEventListener("scroll", this.scroll);

    // Load historic values
    if (this.form !== null) {
      this.accessionNo = this.form.accessionNo;
      this.patName = this.form.patName;
      this.patId = this.form.patId;
      this.studyDate = this.form.studyDates;
      this.modsInStudy = this.form.modsInStudy;
      this.studyId = this.form.studyId;
      this.studyDesc = this.form.studyDesc;
      this.instName = this.form.instName;
      this.stationName = this.form.stationName;
      this.refPhysician = this.form.refPhysician;
    }

    // Studies UPDATE
    if (this.$store.getters.resources.studies.update) {
      this.editMode = true;
    } else {
      this.studiesTableHeaders[8].width = "120px";
    }

    // Load Archives configuration
    axios({
      url: "/api/v1/dicom/find/archives",
      method: "GET",
    })
      .then((response) => {
        if (
          !response.data ||
          !response.data.status ||
          !(response.data.status == "done")
        ) {
          this.sendError(response.data.error);
        } else {
          this.archives = response.data.archives;

          // Load historic studies or search today
          if (this.form !== null) {
            this.archive = this.form.archive;
            this.setPreset(this.form.preset);
          } else {
            this.setPreset("today");
          }
        }
      })
      .catch((error) => {
        this.sendError(error);
      });
  },
  destroyed() {
    window.removeEventListener("keydown", this.keyDown);
    window.removeEventListener("scroll", this.scroll);
  },
};
</script>

<style scoped>
.sticky-banner {
  top: 48px;
  padding-top: 10px;
  position: sticky;
  z-index: 1;
  background-color: var(--v-background-base);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.v-data-table {
  cursor: pointer;
}
</style>